import React from 'react';

import { APIProvider } from '@vis.gl/react-google-maps';
import qs from 'qs';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import TechOnboardLiteContainer from 'registration/containers/TechOnboardLiteContainer';
import { Resource } from 'registration/types';

import AppProvider from 'shared/components/AppProvider';
import { Toaster } from 'shared/components/ihcl/v2/toast';
import { trackAppLoad } from 'shared/helpers/tracking';
import withTracking from 'shared/helpers/withTracking';
import ViewportProvider from 'shared/providers/ViewportProvider';

import EmployerForm from '../components/EmployerForm';
import SignXForm from '../components/non_hiring/SignXForm';
import NonHiringContainer from '../containers/NonHiringContainer';
import OnboardDisambiguationContainer from '../containers/OnboardDisambiguationContainer';
import SignUpNavTabContainer from '../containers/SignUpNavTabContainer';
import TalentOnboardContainer from '../containers/TalentOnboardContainer';
import TalentOnboardLiteContainer, {
  nonHiringIntentRegex,
} from '../containers/TalentOnboardLiteContainer';
import TalentReactivationContainer from '../containers/TalentReactivationContainer';
import TechOnboardContainer from '../containers/TechOnboardContainer';

trackAppLoad('Registration');

type RegistrationAppProps = {
  appContextKeys?: string[];
  resource: Partial<Resource>;
  errors: {
    email?: boolean | string;
  } | null;
  store: Object;
};

const RegistrationApp = ({
  appContextKeys = [],
  resource = {},
  errors = null,
  store,
}: RegistrationAppProps) => (
  <AppProvider store={store} appContextKeys={appContextKeys}>
    <APIProvider
      apiKey={window.IH.googleMapsKey}
      libraries={['places']}
      language="en"
    >
      <ViewportProvider>
        <Toaster />
        <BrowserRouter>
          <Switch>
            <Redirect exact from="/users/sign_up" to="/onboard" />

            <Route
              // eslint-disable-next-line react/no-unstable-nested-components
              component={({ location }) => (
                // Preserve the query string during the redirect
                <Redirect to={{ ...location, pathname: '/talent_onboard' }} />
              )}
              path="/users/sign_up/talent"
            />

            <Route
              // eslint-disable-next-line react/no-unstable-nested-components
              component={withTracking(({ location }: any) => {
                const query = qs.parse(location.search, {
                  ignoreQueryPrefix: true,
                });
                return (
                  <SignUpNavTabContainer>
                    <EmployerForm
                      initialErrors={errors}
                      previousData={resource}
                      urlEmail={query.email as string}
                    />
                  </SignUpNavTabContainer>
                );
              })}
              path="/users/sign_up/employer"
            />

            <Route
              // eslint-disable-next-line react/no-unstable-nested-components
              component={withTracking(({ match }: any) => (
                <NonHiringContainer>
                  <SignXForm
                    initialErrors={errors}
                    intent={match.params[0]}
                    isSignUp={false}
                    previousData={resource}
                  />
                </NonHiringContainer>
              ))}
              path={`/users/sign_in/${nonHiringIntentRegex}`}
            />

            <Route
              path={`/users/:type(sign_up|onboard)/:intent${nonHiringIntentRegex}/:funnelChar?`}
            >
              <TalentOnboardLiteContainer />
            </Route>

            <Route
              path={`/users/:type(sign_up|onboard)/tech/:intent${nonHiringIntentRegex}/:funnelChar?`}
            >
              <TechOnboardLiteContainer />
            </Route>

            <Route component={OnboardDisambiguationContainer} path="/onboard" />

            <Route component={TalentOnboardContainer} path="/talent_onboard" />

            <Route component={TechOnboardContainer} path="/tech_onboard" />

            <Route
              component={TalentReactivationContainer}
              path="/talent_reactivation"
            />
          </Switch>
        </BrowserRouter>
      </ViewportProvider>
    </APIProvider>
  </AppProvider>
);

export default RegistrationApp;
